// Synthesized sound kit (DELIGHT.md). No audio files; every sound is a few
// oscillators and an envelope. The AudioContext is created lazily on the
// first user gesture because browsers require it.

export type SoundKind =
  | 'tick'
  | 'score'
  | 'theirScore'
  | 'onTheClock'
  | 'pick'
  | 'claimWon'
  | 'win'
  | 'notify'
  | 'lock'
  | 'unlock';

let ctx: AudioContext | null = null;
let primed = false;
let master: GainNode | null = null;
const lastPlayed = new Map<SoundKind, number>();
const MIN_GAP_MS: Partial<Record<SoundKind, number>> = { tick: 2000, score: 800, theirScore: 800 };

function ensure(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  if (!ctx) {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = 0.25;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Call once from a user gesture (first tap or click) so later sounds are allowed. */
export function primeAudio(): void {
  primed = true;
  ensure();
}

interface Note {
  freq: number;
  at: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
}

function play(notes: Note[], { noise = false } = {}): void {
  // Browsers refuse audio before a gesture; stay silent instead of warning.
  if (!primed) return;
  const c = ensure();
  if (!c || !master) return;
  const t0 = c.currentTime;
  for (const n of notes) {
    const g = c.createGain();
    g.gain.setValueAtTime(0, t0 + n.at);
    g.gain.linearRampToValueAtTime(n.gain ?? 0.6, t0 + n.at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + n.at + n.dur);
    g.connect(master);
    if (noise) {
      const buffer = c.createBuffer(1, c.sampleRate * n.dur, c.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++)
        data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
      const src = c.createBufferSource();
      src.buffer = buffer;
      const filter = c.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = n.freq;
      src.connect(filter).connect(g);
      src.start(t0 + n.at);
      src.stop(t0 + n.at + n.dur);
    } else {
      const o = c.createOscillator();
      o.type = n.type ?? 'sine';
      o.frequency.setValueAtTime(n.freq, t0 + n.at);
      o.connect(g);
      o.start(t0 + n.at);
      o.stop(t0 + n.at + n.dur + 0.02);
    }
  }
}

const C5 = 523.25;
const E5 = 659.25;
const G5 = 783.99;
const G3 = 196.0;
const A5 = 880.0;
const C6 = 1046.5;

export function playSound(kind: SoundKind): void {
  const now = performance.now();
  const gap = MIN_GAP_MS[kind];
  if (gap && now - (lastPlayed.get(kind) ?? -Infinity) < gap) return;
  lastPlayed.set(kind, now);
  switch (kind) {
    case 'tick':
      return play([{ freq: A5, at: 0, dur: 0.04, gain: 0.12 }]);
    case 'score':
      return play([
        { freq: C5, at: 0, dur: 0.12, type: 'triangle', gain: 0.5 },
        { freq: G5, at: 0.12, dur: 0.18, type: 'triangle', gain: 0.5 },
      ]);
    case 'theirScore':
      return play([{ freq: G3, at: 0, dur: 0.15, gain: 0.25 }]);
    case 'onTheClock':
      return play([
        { freq: C5, at: 0, dur: 0.1, gain: 0.5 },
        { freq: E5, at: 0.12, dur: 0.1, gain: 0.5 },
        { freq: G5, at: 0.24, dur: 0.16, gain: 0.5 },
      ]);
    case 'pick':
      return play([{ freq: 900, at: 0, dur: 0.06, gain: 0.5 }], { noise: true });
    case 'claimWon':
      return play([
        { freq: E5, at: 0, dur: 0.12, type: 'triangle', gain: 0.45 },
        { freq: C6, at: 0.12, dur: 0.2, type: 'triangle', gain: 0.45 },
      ]);
    case 'win':
      return play([
        { freq: C5, at: 0, dur: 0.16, type: 'triangle', gain: 0.6 },
        { freq: E5, at: 0.16, dur: 0.16, type: 'triangle', gain: 0.6 },
        { freq: G5, at: 0.32, dur: 0.16, type: 'triangle', gain: 0.6 },
        { freq: C6, at: 0.48, dur: 0.4, type: 'triangle', gain: 0.7 },
      ]);
    case 'notify':
      return play([
        { freq: E5, at: 0, dur: 0.08, gain: 0.35 },
        { freq: C5, at: 0.08, dur: 0.1, gain: 0.35 },
      ]);
    case 'lock':
      return play([{ freq: 1200, at: 0, dur: 0.04, gain: 0.4 }], { noise: true });
    // An achievement found (ACHIEVEMENTS.md §12): a quick rising sparkle,
    // lighter than a win.
    case 'unlock':
      return play([
        { freq: G5, at: 0, dur: 0.08, type: 'triangle', gain: 0.4 },
        { freq: C6, at: 0.08, dur: 0.08, type: 'triangle', gain: 0.4 },
        { freq: C6 * 1.5, at: 0.16, dur: 0.26, type: 'sine', gain: 0.32 },
      ]);
  }
}
